import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import {
  BackToTopBtn,
  BreakpointDetector,
  defaultTheme,
  HeadWrapper,
  StylesWrapper,
  UrlContext,
  PageTypeContext,
  LanguageContext,
} from '~/index';
import CookieBannerProvider from './providers/CookieBannerProvider';
import getCreateUrl from './getCreateUrl';
import HeaderHeightContext from '~/helpers/HeaderHeightContext';
import GtmConsentLoader from '~/providers/GtmConsentLoader';
import HomePageContext from '~/helpers/HomePageContext/HomePageContext';

const getHeaderHeight = () => {
  if (typeof document !== 'undefined') {
    const header = document.getElementById('page-header');
    if (header) {
      return header.clientHeight;
    }
  }
  return 0;
};

/**
 * RendererWrapper component that wraps the application layout with all required context providers,
 * metadata, and optional UI helpers like back-to-top button and cookie disclaimers.
 *
 * It provides:
 * - Metadata for SEO and social sharing (via Head and HeadWrapper)
 * - External tracking services like OneTrust, GTM, and Matomo
 * - Context for URL, language, header height, page type, homepage flag, and content messages
 * - Theming and styling via StylesWrapper
 * - Optional UI helpers like the back-to-top button and cookie disclaimer banner
 *
 * @component
 *
 * @param {Object} props - The props for RendererWrapper
 * @param {React.ReactNode} props.children - Child components to be rendered inside the wrapper
 * @param {MetaTagsApiProps} props.meta - Metadata used for SEO (e.g. title, description, etc.)
 * @param {Object} [props.openGraph] - Optional Open Graph metadata for social sharing
 * @param {ExternalServicesProps} props.externalServices - Config for third-party services (GTM, OneTrust, etc.)
 * @param {boolean} [props.enableBackToTop=true] - Whether to show the back-to-top button
 * @param {FrontendContext} props.context - Context information like base URL, pathname, and home page flag
 * @param {string} props.language - The current page language, e.g. "de-DE"
 * @param {'DEFAULT' | 'CONTENT_HUB' | 'CONTENT_HUB_TOPIC'} [props.style='DEFAULT'] - Page styling mode
 * @param {string | undefined} [props.pageType='career'] - The current page type, e.g. "career" or "lp"
 *
 * @returns {React.ReactElement} The fully wrapped component tree
 */
const RendererWrapper = ({
  children,
  meta,
  openGraph,
  externalServices,
  enableBackToTop = true,
  context,
  language,
  style = 'DEFAULT',
  pageType,
}) => {
  const createUrl = getCreateUrl(context);
  const { googleTagManagerId, matomoUrl, oneTrustId } = externalServices;
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    let lastHeight = 0;

    const checkHeaderHeight = () => {
      const currentHeight = getHeaderHeight();

      // Only update state if height actually changed
      if (currentHeight !== lastHeight && currentHeight > 0) {
        lastHeight = currentHeight;
        setHeaderHeight(currentHeight);
      }
    };

    checkHeaderHeight();
    // Poll every 200ms (5 times per second instead of 60)
    const intervalId = setInterval(checkHeaderHeight, 200);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <BreakpointDetector>
      <HomePageContext.Provider value={context.isHomePage}>
        <UrlContext.Provider value={createUrl}>
          <LanguageContext.Provider value={language.split('-')[0]}>
            <HeaderHeightContext.Provider value={headerHeight}>
              <PageTypeContext.Provider value={pageType || 'career'}>
                <StylesWrapper theme={defaultTheme} style={style}>
                  <Head>
                    <title>{meta.title}</title>
                    <link rel="shortcut icon" href="/favicon.ico" />
                    <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
                    <link
                      rel="icon"
                      type="image/png"
                      sizes="16x16"
                      href="/favicon-16x16.png"
                    />
                    <link
                      rel="icon"
                      type="image/png"
                      sizes="32x32"
                      href="/favicon-32x32.png"
                    />
                    <link
                      rel="apple-touch-icon"
                      sizes="152x152"
                      href="/apple-touch-icon-152x152.png"
                    />
                    <link
                      rel="apple-touch-icon"
                      sizes="167x167"
                      href="/apple-touch-icon-167x167.png"
                    />
                    <link
                      rel="apple-touch-icon"
                      sizes="180x180"
                      href="/apple-touch-icon.png"
                    />
                    <link rel="manifest" href="/site.webmanifest" />
                    <meta
                      name="apple-mobile-web-app-title"
                      content="Fresenius"
                    />
                    <meta name="theme-color" content="#002967" />
                    <meta name="msapplication-TileColor" content="#ffffff" />
                    <meta
                      name="msapplication-config"
                      content="/browserconfig.xml"
                    />
                    {openGraph && meta && (
                      <HeadWrapper
                        openGraph={openGraph}
                        meta={meta}
                        url={`${context.baseUrl}${context.pathname}`}
                        imgProperties={defaultTheme.openGraphImg}
                        language={language}
                      />
                    )}
                  </Head>
                  {enableBackToTop && <BackToTopBtn />}
                  <GtmConsentLoader googleTagManagerId={googleTagManagerId} />
                  <CookieBannerProvider
                    oneTrustId={oneTrustId}
                    googleTagManagerId={googleTagManagerId}
                    matomoUrl={matomoUrl}
                  >
                    {children}
                  </CookieBannerProvider>
                </StylesWrapper>
              </PageTypeContext.Provider>
            </HeaderHeightContext.Provider>
          </LanguageContext.Provider>
        </UrlContext.Provider>
      </HomePageContext.Provider>
    </BreakpointDetector>
  );
};

export default RendererWrapper;
